/**
 * Twilio Voice JS SDK client wrapper for the staff browser (Phase 4B).
 *
 * The Twilio path is the alternate transport to our WebRTC-direct flow.
 * Inbound calls land via TwiML `<Dial><Client>staff-<id></Client></Dial>`,
 * which fires a `Device.on('incoming', ...)` event with a SDK `Call` object.
 *
 * Lifecycle:
 *
 *   1. App-level bootstrap (when the store is twilio-transport AND the staff
 *      user is `availableForCalls`) calls `connect(token)`. The manager builds
 *      a `Device`, registers handlers, and starts listening.
 *   2. When TwiML dials this client, `onIncoming` surfaces it on the same
 *      `voiceStore.incomingCall` state used by the WebRTC path. The toast
 *      renders as normal.
 *   3. The toast's "Answer" button calls `acceptIncoming()` → `call.accept()`.
 *      The SDK handles audio internally — no SDP exchange, no MediaStream.
 *   4. End/transfer/hangup: `endCall()` → `call.disconnect()`. The backend
 *      learns the call ended via Twilio's status callback.
 *
 * Token handling: Twilio fires `tokenWillExpire` ~3 minutes before the JWT
 * expires. We refetch a fresh token from the backend and call
 * `device.updateToken()` — no interruption to in-flight calls.
 *
 * One device per page. Re-entry of `connect()` while already connected is a
 * no-op (idempotent).
 */
import { Device, type Call } from '@twilio/voice-sdk';
import type { IncomingCallPayload } from '../../lib/websocket/events';
import { voiceService } from './services/voiceService';

type IncomingHandler = (payload: IncomingCallPayload, call: Call) => void;

/**
 * Map from a Twilio incoming Call's `parameters` (custom params encoded in
 * the TwiML `<Client>` verb) to the IncomingCallPayload shape the rest of
 * the app expects.
 *
 * The backend sets these via `<Client><Parameter name="..." />` in TwiML.
 * If a field is missing we substitute safe defaults — the call still
 * works, just with degraded display.
 */
function callToIncomingPayload(call: Call): IncomingCallPayload {
  const params = call.parameters;
  const customParams = call.customParameters;
  const get = (key: string): string | undefined =>
    customParams.get(key) ?? params[key as keyof typeof params];

  return {
    id: get('callId') ?? params.CallSid ?? '',
    conversationId: get('conversationId') ?? '',
    customerName: get('customerName') ?? '',
    customerPhone: get('customerPhone') ?? params.From ?? '',
    transport: 'twilio',
    ringingAt: get('ringingAt') ?? new Date().toISOString(),
    // Twilio path doesn't surface an SDP — the SDK negotiates internally.
    offerSdp: '',
  };
}

class TwilioClientManager {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private incomingHandler: IncomingHandler | null = null;
  // Tracks the token we last constructed/updated the device with so reconnect
  // attempts (e.g. after `availableForCalls` is toggled off then on within
  // the JWT TTL) can be no-ops.
  private connectingPromise: Promise<void> | null = null;

  /**
   * Build (or rebuild) the Twilio Device with a fresh JWT and register
   * lifecycle handlers. Idempotent — calling while already connected returns
   * the in-flight promise.
   */
  connect(token: string, onIncoming: IncomingHandler): Promise<void> {
    this.incomingHandler = onIncoming;

    if (this.device) {
      // Already up — just refresh the token and the handler.
      try {
        this.device.updateToken(token);
      } catch (err) {
        console.warn('[twilio-client] updateToken failed:', err);
      }
      return Promise.resolve();
    }

    if (this.connectingPromise) return this.connectingPromise;

    this.connectingPromise = this.boot(token);
    return this.connectingPromise;
  }

  private async boot(token: string): Promise<void> {
    try {
      const device = new Device(token, {
        logLevel: 'WARN',
        // Don't show a browser-native "Are you sure?" prompt on tab close —
        // we already manage call state in the store.
        closeProtection: false,
      });

      device.on('incoming', (call: Call) => this.handleIncoming(call));
      device.on('tokenWillExpire', () => {
        void this.refreshToken();
      });
      device.on('error', (err: unknown) => {
        console.warn('[twilio-client] device error:', err);
      });
      device.on('unregistered', () => {
        console.info('[twilio-client] device unregistered');
      });
      device.on('registered', () => {
        console.info('[twilio-client] device registered');
      });

      // Register so we can receive inbound calls. Throws if the token is
      // invalid; let the error propagate to the caller for visibility.
      await device.register();
      this.device = device;
    } finally {
      this.connectingPromise = null;
    }
  }

  private async refreshToken(): Promise<void> {
    if (!this.device) return;
    try {
      const fresh = await voiceService.getTwilioToken();
      this.device.updateToken(fresh.token);
    } catch (err) {
      console.warn('[twilio-client] failed to refresh token:', err);
    }
  }

  private handleIncoming(call: Call): void {
    this.activeCall = call;
    const payload = callToIncomingPayload(call);

    // If the call is cancelled (caller hung up before answer) or rejected,
    // clear our reference so a future `acceptIncoming` doesn't operate on
    // a stale handle.
    call.on('cancel', () => {
      if (this.activeCall === call) this.activeCall = null;
    });
    call.on('reject', () => {
      if (this.activeCall === call) this.activeCall = null;
    });
    call.on('disconnect', () => {
      if (this.activeCall === call) this.activeCall = null;
    });

    this.incomingHandler?.(payload, call);
  }

  /**
   * Returns the currently-ringing or in-progress Call. Useful for the toast
   * after `setIncoming` so it doesn't have to chase the SDK event itself.
   */
  getActiveCall(): Call | null {
    return this.activeCall;
  }

  /** Debug accessor — returns the underlying Twilio Device for inspection. */
  getDevice(): Device | null {
    return this.device;
  }

  /**
   * Accept the most recently surfaced incoming call.
   *
   * Waits up to `timeoutMs` for the SDK's `incoming` event to populate
   * `activeCall`. There's an inherent race here: our backend broadcasts
   * `call:incoming` over Socket.IO immediately after creating the Call
   * row, but the Twilio SDK's own `incoming` event arrives a few hundred
   * ms later (after Twilio actually dials the client). If staff click
   * Answer in that window we'd otherwise return null and the toast
   * shows "Failed to answer call" for no real reason.
   */
  async acceptIncoming(timeoutMs: number = 3000): Promise<Call | null> {
    const start = Date.now();
    while (!this.activeCall && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 50));
    }
    const call = this.activeCall;
    if (!call) {
      console.warn(
        `[twilio-client] acceptIncoming: no active call after ${timeoutMs}ms — ` +
          'SDK never received an incoming event (check Twilio Console call log)',
      );
      return null;
    }
    try {
      call.accept();
    } catch (err) {
      console.warn('[twilio-client] call.accept() failed:', err);
      this.activeCall = null;
      return null;
    }
    return call;
  }

  /**
   * Initiate an outbound call to a customer's phone number via the SDK.
   * The Device.connect() returns a `Call` object representing the local
   * leg; we stash it as activeCall so the same UI machinery (InCallPanel,
   * mute, end, recording-via-Twilio) handles outbound just like inbound.
   *
   * `params` are forwarded to Twilio as custom parameters on the call —
   * the backend webhook receives them so we can attach the Call row to
   * the right ConversationSession.
   */
  async startOutboundCall(
    customerPhone: string,
    conversationId: string,
  ): Promise<Call | null> {
    if (!this.device) {
      console.warn('[twilio-client] startOutboundCall: device not connected');
      return null;
    }
    if (this.activeCall) {
      console.warn('[twilio-client] startOutboundCall: already on a call');
      return null;
    }
    try {
      const call = await this.device.connect({
        params: {
          To: customerPhone,
          conversationId,
        },
      });
      this.activeCall = call;
      // Mirror the inbound lifecycle handlers so a remote hangup / failure
      // clears our reference.
      call.on('disconnect', () => {
        if (this.activeCall === call) this.activeCall = null;
      });
      call.on('cancel', () => {
        if (this.activeCall === call) this.activeCall = null;
      });
      call.on('reject', () => {
        if (this.activeCall === call) this.activeCall = null;
      });
      return call;
    } catch (err) {
      console.warn('[twilio-client] startOutboundCall failed:', err);
      return null;
    }
  }

  /** Hang up the current call (if any). */
  endCall(): void {
    if (!this.activeCall) return;
    try {
      this.activeCall.disconnect();
    } catch (err) {
      console.warn('[twilio-client] disconnect failed:', err);
    }
    this.activeCall = null;
  }

  /** Toggle mute on the current call. */
  setMuted(muted: boolean): void {
    if (!this.activeCall) return;
    try {
      this.activeCall.mute(muted);
    } catch (err) {
      console.warn('[twilio-client] mute toggle failed:', err);
    }
  }

  /** Tear down the device and clear state. Safe to call when not connected. */
  disconnect(): void {
    this.incomingHandler = null;
    if (this.activeCall) {
      try {
        this.activeCall.disconnect();
      } catch {
        /* ignore */
      }
      this.activeCall = null;
    }
    if (this.device) {
      try {
        this.device.destroy();
      } catch (err) {
        console.warn('[twilio-client] destroy failed:', err);
      }
      this.device = null;
    }
  }

  isConnected(): boolean {
    return this.device !== null;
  }
}

/** Module-singleton — one Twilio Device per page. */
export const twilioClient = new TwilioClientManager();

// Runtime debug handle. From DevTools you can inspect:
//   window.__twilioClient.getDevice()?.identity   // registered identity
//   window.__twilioClient.getDevice()?.state      // 'registered' | 'destroyed' | ...
//   window.__twilioClient.getActiveCall()         // current Call object or null
// Removed in production builds? No — useful enough on prod for live debugging.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__twilioClient = twilioClient;
}
